import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import {
  Building2, CreditCard, Search, RefreshCw, Filter,
  CheckCircle, XCircle, Clock, AlertTriangle, Play, Pause,
  MoreHorizontal, Eye, TrendingUp, Users, Calendar, Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import PlanPricingCard from './PlanPricingCard';

const API = process.env.REACT_APP_BACKEND_URL;

// Status configuration
const STATUS_CONFIG = {
  trialing: { label: 'Trial', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
  active: { label: 'Active', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
  past_due: { label: 'Past Due', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle },
  canceled: { label: 'Canceled', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: XCircle },
  paused: { label: 'Paused', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Pause },
  none: { label: 'No Subscription', color: 'bg-slate-100 text-slate-500 border-slate-200', icon: XCircle }
};

// Plan badges; the price always comes from the billing plan itself.
const PLAN_CONFIG = {
  starter: { name: 'Starter', color: 'bg-slate-100 text-slate-700' },
  growth: { name: 'Growth', color: 'bg-blue-100 text-blue-700' },
  pro: { name: 'Pro', color: 'bg-purple-100 text-purple-700' }
};
const planPrice = (plan) => `$${((plan.price_cents || 0) / 100).toLocaleString()}/mo`;

export default function SubscriptionsPage() {
  const { token } = useOutletContext();
  
  const [merchants, setMerchants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  
  // Dialog states
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState(null); // 'trial', 'activate', 'cancel', 'change_plan', 'set_status'
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [actionForm, setActionForm] = useState({
    plan_id: 'starter',
    trial_days: 7,
    status: ''
  });
  const [actionLoading, setActionLoading] = useState(false);

  const getHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const fetchData = useLatestCallback(async () => {
    setLoading(true);
    try {
      // Fetch plans
      const plansRes = await axios.get(`${API}/api/admin/billing/plans`);
      setPlans(plansRes.data.plans || []);

      // Fetch restaurants with subscription info
      const restaurantsRes = await axios.get(`${API}/api/company-admin/restaurants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const restaurantsList = restaurantsRes.data || [];
      
      // Fetch subscription for each restaurant
      const merchantsWithSubs = await Promise.all(
        restaurantsList.map(async (restaurant) => {
          try {
            const subRes = await axios.get(
              `${API}/api/admin/merchants/${restaurant.id}/entitlements?product_code=restaurant`,
              getHeaders()
            );
            return {
              ...restaurant,
              subscription: subRes.data.subscription,
              enabled: subRes.data.enabled,
              features: subRes.data.features,
              limits: subRes.data.limits
            };
          } catch {
            return {
              ...restaurant,
              subscription: null,
              enabled: false
            };
          }
        })
      );
      
      setMerchants(merchantsWithSubs);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchData();
  }, [token, fetchData]);

  const openActionDialog = (merchant, action) => {
    setSelectedMerchant(merchant);
    setActionType(action);
    setActionForm({
      plan_id: merchant.subscription?.plan_id || 'starter',
      trial_days: 7,
      status: merchant.subscription?.status || ''
    });
    setShowActionDialog(true);
  };

  const handleAction = async () => {
    if (!selectedMerchant) return;
    
    setActionLoading(true);
    try {
      const merchantId = selectedMerchant.id.toString();
      
      switch (actionType) {
        case 'trial':
          await axios.post(`${API}/api/admin/billing/trials`, {
            merchant_id: merchantId,
            product_code: 'restaurant',
            plan_id: actionForm.plan_id,
            trial_days: actionForm.trial_days
          }, getHeaders());
          toast.success(`Trial started for ${selectedMerchant.name}`);
          break;
          
        case 'activate':
          await axios.post(`${API}/api/admin/billing/activate`, {
            merchant_id: merchantId,
            product_code: 'restaurant',
            plan_id: actionForm.plan_id
          }, getHeaders());
          toast.success(`Subscription activated for ${selectedMerchant.name}`);
          break;
          
        case 'cancel':
          await axios.post(`${API}/api/admin/billing/cancel`, {
            merchant_id: merchantId,
            product_code: 'restaurant'
          }, getHeaders());
          toast.success(`Subscription canceled for ${selectedMerchant.name}`);
          break;
          
        case 'change_plan':
          await axios.post(`${API}/api/admin/billing/activate`, {
            merchant_id: merchantId,
            product_code: 'restaurant',
            plan_id: actionForm.plan_id
          }, getHeaders());
          toast.success(`Plan changed to ${PLAN_CONFIG[actionForm.plan_id]?.name}`);
          break;
          
        case 'set_status':
          await axios.post(`${API}/api/admin/billing/set-status`, {
            merchant_id: merchantId,
            product_code: 'restaurant',
            status: actionForm.status
          }, getHeaders());
          toast.success(`Status set to ${actionForm.status}`);
          break;
      }
      
      setShowActionDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  // Filter merchants
  const filteredMerchants = merchants.filter(m => {
    const subStatus = m.subscription?.status || 'none';
    
    if (statusFilter !== 'all' && subStatus !== statusFilter) return false;
    if (planFilter !== 'all' && m.subscription?.plan_id !== planFilter) return false;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        m.name?.toLowerCase().includes(query) ||
        m.email?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Stats
  const stats = {
    total: merchants.length,
    active: merchants.filter(m => m.subscription?.status === 'active').length,
    trialing: merchants.filter(m => m.subscription?.status === 'trialing').length,
    pastDue: merchants.filter(m => m.subscription?.status === 'past_due').length,
    noSub: merchants.filter(m => !m.subscription).length
  };

  const getDialogContent = () => {
    switch (actionType) {
      case 'trial':
        return {
          title: 'Start Trial',
          description: `Start a free trial for ${selectedMerchant?.name}`,
          content: (
            <div className="space-y-4">
              <div>
                <Label>Plan</Label>
                <Select value={actionForm.plan_id} onValueChange={(v) => setActionForm({ ...actionForm, plan_id: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map(plan => (
                      <SelectItem key={plan.plan_id} value={plan.plan_id}>
                        {plan.name} - {planPrice(plan)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Trial Duration (days)</Label>
                <Input 
                  type="number" 
                  value={actionForm.trial_days}
                  onChange={(e) => setActionForm({ ...actionForm, trial_days: parseInt(e.target.value) })}
                  min={1}
                  max={30}
                />
              </div>
            </div>
          ),
          confirmText: 'Start Trial',
          confirmClass: 'bg-blue-600 hover:bg-blue-700'
        };
        
      case 'activate':
        return {
          title: 'Activate Subscription',
          description: `Activate subscription for ${selectedMerchant?.name}`,
          content: (
            <div>
              <Label>Plan</Label>
              <Select value={actionForm.plan_id} onValueChange={(v) => setActionForm({ ...actionForm, plan_id: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {plans.map(plan => (
                    <SelectItem key={plan.plan_id} value={plan.plan_id}>
                      {plan.name} - {planPrice(plan)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ),
          confirmText: 'Activate',
          confirmClass: 'bg-green-600 hover:bg-green-700'
        };
        
      case 'cancel':
        return {
          title: 'Cancel Subscription',
          description: `Are you sure you want to cancel subscription for ${selectedMerchant?.name}?`,
          content: (
            <p className="text-sm text-gray-500">
              This will immediately disable access to premium features. The merchant will see the subscription blocked page.
            </p>
          ),
          confirmText: 'Cancel Subscription',
          confirmClass: 'bg-red-600 hover:bg-red-700'
        };
        
      case 'change_plan':
        return {
          title: 'Change Plan',
          description: `Change subscription plan for ${selectedMerchant?.name}`,
          content: (
            <div>
              <Label>New Plan</Label>
              <Select value={actionForm.plan_id} onValueChange={(v) => setActionForm({ ...actionForm, plan_id: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {plans.map(plan => (
                    <SelectItem key={plan.plan_id} value={plan.plan_id}>
                      {plan.name} - {planPrice(plan)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ),
          confirmText: 'Change Plan',
          confirmClass: 'bg-blue-600 hover:bg-blue-700'
        };
        
      case 'set_status':
        return {
          title: 'Set Status',
          description: `Manually set subscription status for ${selectedMerchant?.name}`,
          content: (
            <div>
              <Label>Status</Label>
              <Select value={actionForm.status} onValueChange={(v) => setActionForm({ ...actionForm, status: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trialing">Trialing</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="past_due">Past Due</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ),
          confirmText: 'Set Status',
          confirmClass: 'bg-purple-600 hover:bg-purple-700'
        };
        
      default:
        return { title: '', description: '', content: null, confirmText: '', confirmClass: '' };
    }
  };

  const dialogContent = getDialogContent();

  return (
    <div className="space-y-6" data-testid="company-subscriptions">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subscriptions</h1>
          <p className="text-gray-500">Manage merchant subscriptions and billing</p>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={fetchData}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <PlanPricingCard plans={plans} token={token} onSaved={fetchData} />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Building2 className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Trialing</p>
                <p className="text-2xl font-bold text-blue-600">{stats.trialing}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Past Due</p>
                <p className="text-2xl font-bold text-red-600">{stats.pastDue}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">No Sub</p>
                <p className="text-2xl font-bold text-gray-500">{stats.noSub}</p>
              </div>
              <XCircle className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search merchants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-subscriptions"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="trialing">Trialing</SelectItem>
                <SelectItem value="past_due">Past Due</SelectItem>
                <SelectItem value="canceled">Canceled</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="none">No Subscription</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger className="w-36" data-testid="plan-filter">
                <SelectValue placeholder="All Plans" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="starter">Starter</SelectItem>
                <SelectItem value="growth">Growth</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Subscriptions Table */}
      <Card className="bg-white border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Merchant</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Trial/Period End</TableHead>
                  <TableHead>Features</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMerchants.length > 0 ? (
                  filteredMerchants.map((merchant) => {
                    const subStatus = merchant.subscription?.status || 'none';
                    const statusConfig = STATUS_CONFIG[subStatus] || STATUS_CONFIG.none;
                    const StatusIcon = statusConfig.icon;
                    const planId = merchant.subscription?.plan_id;
                    const planConfig = planId ? PLAN_CONFIG[planId] : null;
                    
                    const periodEnd = merchant.subscription?.current_period_end || merchant.subscription?.trial_ends_at;
                    const isExpiringSoon = periodEnd && new Date(periodEnd) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                    
                    return (
                      <TableRow key={merchant.id} data-testid={`merchant-row-${merchant.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                              {merchant.name?.charAt(0)}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{merchant.name}</p>
                              <p className="text-sm text-gray-500">ID: {merchant.id}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {planConfig ? (
                            <Badge className={planConfig.color}>
                              {planConfig.name}
                            </Badge>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusConfig.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {periodEnd ? (
                            <div className={isExpiringSoon ? 'text-red-600' : 'text-gray-600'}>
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(periodEnd).toLocaleDateString()}
                              </div>
                              {isExpiringSoon && (
                                <span className="text-xs text-red-500">Expiring soon</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {merchant.enabled ? (
                            <div className="flex items-center gap-1 text-green-600">
                              <Zap className="h-3 w-3" />
                              <span className="text-sm">Enabled</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-gray-400">
                              <XCircle className="h-3 w-3" />
                              <span className="text-sm">Disabled</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`merchant-actions-${merchant.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {!merchant.subscription && (
                                <DropdownMenuItem onClick={() => openActionDialog(merchant, 'trial')}>
                                  <Play className="h-4 w-4 mr-2" />
                                  Start Trial
                                </DropdownMenuItem>
                              )}
                              {merchant.subscription?.status === 'trialing' && (
                                <DropdownMenuItem onClick={() => openActionDialog(merchant, 'activate')}>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Activate
                                </DropdownMenuItem>
                              )}
                              {merchant.subscription?.status === 'active' && (
                                <>
                                  <DropdownMenuItem onClick={() => openActionDialog(merchant, 'change_plan')}>
                                    <TrendingUp className="h-4 w-4 mr-2" />
                                    Change Plan
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openActionDialog(merchant, 'cancel')} className="text-red-600">
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Cancel
                                  </DropdownMenuItem>
                                </>
                              )}
                              {(merchant.subscription?.status === 'canceled' || merchant.subscription?.status === 'paused') && (
                                <DropdownMenuItem onClick={() => openActionDialog(merchant, 'activate')}>
                                  <Play className="h-4 w-4 mr-2" />
                                  Reactivate
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openActionDialog(merchant, 'set_status')}>
                                <Eye className="h-4 w-4 mr-2" />
                                Set Status
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-gray-500">
                      <CreditCard className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                      <p>No merchants found</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialogContent.title}</DialogTitle>
            <DialogDescription>{dialogContent.description}</DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {dialogContent.content}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAction}
              disabled={actionLoading || (actionType === 'set_status' && !actionForm.status)}
              className={dialogContent.confirmClass}
            >
              {actionLoading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {dialogContent.confirmText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
